import { EVMNft } from '@repo/types-client';

/**
 * Determines the rarity of a Doodle NFT based on its attributes.
 * @function getDoodleRarity
 * @param {EVMNft} doodle - The Doodle NFT object.
 * @returns {Object} An object containing the rarity title and standard rarity.
 * @returns {string} return.rarityTitle - The title of the rarity.
 * @returns {string} return.standardRarity - The standard rarity category.
 */
export const getDoodleRarity = (
  doodle: EVMNft,
): {
  rarityTitle: 'Doodles OG' | 'Rare Trait' | 'Special Head' | 'Hand Drawn';
  standardRarity: 'essential' | 'exclusive' | 'grail' | 'iconic';
} => {
  const { structuredAttributes } = doodle;

  const hasCustomTrait =
    doodleTraits.customs.head.includes(structuredAttributes.head) ||
    doodleTraits.customs.hair.includes(structuredAttributes.hair);

  if (hasCustomTrait) {
    return {
      rarityTitle: 'Hand Drawn',
      standardRarity: 'iconic',
    };
  }

  const hasSpecialTrait =
    doodleTraits.special.face.includes(structuredAttributes.face) ||
    doodleTraits.special.head.includes(structuredAttributes.head);

  if (hasSpecialTrait) {
    return {
      rarityTitle: 'Special Head',
      standardRarity: 'grail',
    };
  }

  const hasRareTrait =
    doodleTraits.rare.body.includes(structuredAttributes.body) ||
    doodleTraits.rare.hair.includes(structuredAttributes.hair);

  if (hasRareTrait) {
    return {
      rarityTitle: 'Rare Trait',
      standardRarity: 'exclusive',
    };
  }

  return {
    rarityTitle: 'Doodles OG',
    standardRarity: 'essential',
  };
};

/**
 * Defines the traits for different rarity levels of Doodles.
 * @constant {Object} doodleTraits
 * @property {Object} rare - The rare traits.
 * @property {string[]} rare.body - The rare body traits.
 * @property {string[]} rare.hair - The rare hair traits.
 * @property {Object} special - The special traits.
 * @property {string[]} special.face - The special face traits.
 * @property {string[]} special.head - The special head traits.
 * @property {Object} customs - The custom traits.
 * @property {string[]} customs.head - The custom head traits.
 * @property {string[]} customs.hair - The custom hair traits.
 */
export const doodleTraits = {
  rare: {
    body: [
      'leopard hoodie',
      'rainbow stripe sweater',
      'spotted hoodie',
      'spotted sweater',
      'holographic sweater',
    ],
    hair: [
      'wizard',
      'holographic brushcut',
      'poopie',
      'crown',
      'holographic bob',
      'holographic poopie',
      'sailor',
      'holographic crown',
      'crown with holographic long',
    ],
  },
  special: {
    face: [
      'whale',
      'duck',
      'dino',
      'holographic dino',
      'shark',
      'puffer up',
      'puffer up visor',
      'rainbow puke',
    ],
    head: [
      'icecream',
      'popsicle',
      'rainbow',
      'lit',
      'balloon',
      'coffee',
      'flower',
      'pickle',
      'devil',
    ],
  },
  customs: {
    head: [
      'green alien',
      'holographic alien',
      'blue alien',
      'gold alien',
      'grey alien',
      'iridescent alien',
      'pink alien',
      'purple alien',
      'cat',
      'calico cat',
      'devil cat',
      'green cat',
      'holographic cat',
      'skelly',
      'brittle bones skelly',
      'ape',
      'green ape',
      'pink ape',
      'blue ape',
      'gold ape',
      'holographic ape',
    ],
    hair: [
      'bed head',
      'bubblegum bed head',
      'gold bed head',
      'holographic bed head',
      'stellar bed head',
    ],
  },
};
